import { STORAGE, STORAGE_SPLITTER } from "@/base/constants/storage";
import { KeepForever } from "@const/store";
import { dispatchUseListenStorageEvent } from "@hooks/useListenStorage";

const DEFAULT_STORAGE = localStorage;
const storageMap = {
  [STORAGE.LOCAL]: localStorage,
  [STORAGE.SESSION]: sessionStorage,
};
const storageList = Object.values(storageMap);

class Observable {
  constructor() {
    this.handlers = {};
  }

  set(key, val) {
    const handlers = this.handlers;
    (handlers[key] || []).forEach((handler) => handler(val));
  }

  subscribe(key, func) {
    let handlers = this.handlers[key];

    if (!handlers) {
      handlers = this.handlers[key] = [];
    }

    handlers.push(func);
    return () => this.unsubscribe(key, func);
  }

  unsubscribe(key, func) {
    this.handlers[key] = (this.handlers[key] || []).filter(
      (handler) => handler !== func,
    );
  }
}

const observable = new Observable();

export class StorageService {
  constructor({ version = "v1" } = {}) {
    this._version = version;
    this.observable = observable;
  }

  getStorage(key) {
    const [keyStorage] = key.split(STORAGE_SPLITTER);
    return storageMap[keyStorage] || DEFAULT_STORAGE;
  }

  _decorateKey(key) {
    return `${key}&ver=${this._version}`;
  }

  _objectToString(obj) {
    return JSON.stringify(obj);
  }

  _parseString(str) {
    if (str) {
      return JSON.parse(str);
    }

    return str;
  }

  get(key, _default) {
    try {
      const strData = this.getStorage(key).getItem(this._decorateKey(key));

      const data = this._parseString(strData);

      return arguments.length === 1 ? data : data || _default;
    } catch (e) {
      return _default;
    }
  }

  set(key, value) {
    this.getStorage(key).setItem(
      this._decorateKey(key),
      this._objectToString(value),
    );
    // dispatch the custom event, so we can safely listen to the storage changes
    dispatchUseListenStorageEvent(this.getStorage(key), key, value);
    this.observable.set(key, value);
  }

  merge(key, value) {
    const keyName = this._decorateKey(key);

    const oldValue = this.get(key);
    const newValue = Object.assign(oldValue, value);
    this.getStorage(key).setItem(keyName, this._objectToString(newValue));
  }

  remove(key) {
    this.getStorage(key).removeItem(this._decorateKey(key));
  }

  clear() {
    // saving data that should not be cleared
    const dataToSave = KeepForever.reduce((acc, { key, defaultValue }) => {
      acc.push({
        key,
        value: this.get(key, defaultValue),
      });
      return acc;
    }, []);

    // clear all storages
    storageList.forEach((storage) => {
      this.clearStorage(storage);
    });

    // restore data that should not be cleared
    dataToSave.forEach(({ key, value }) => {
      this.set(key, value);
    });
  }

  clearStorage(storage) {
    storage.clear();
  }

  onChange(key, func) {
    return this.observable.subscribe(key, func);
  }

  onChangeDataInSiblingTab(key, func) {
    const listener = (event) => {
      if (event.key !== this._decorateKey(key)) return;
      func(this.get(key));
    };

    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }
}

StorageService.$displayName = "StorageService";

export default new StorageService();
